import type { ReactNode } from "react";

import { ErrorState, HubTable } from "@/ds";
import type { EmbarcacoesSortField } from "@/modules/embarcacoes";
import {
  hubTableCartao,
  HubTableCartao,
  HubTableDetailButton,
} from "@/shared/components/hub-table-cartao";
import type { EmbarcacoesColumnKey } from "../../embarcacoes.constants";
import {
  EMBARCACOES_ACTIONS_KEY,
  EMBARCACOES_ERROR_MESSAGE,
  EMBARCACOES_LOADING_LABEL,
  EMBARCACOES_ROWS_PER_PAGE_LABEL,
} from "../../embarcacoes.constants";
import type { EmbarcacaoLinha } from "../../embarcacoes.utils";
import type { EmbarcacoesTabelaProps } from "./types-embarcacoes-tabela";

/** Tabela da listagem de Embarcações (Figma 7945:222883): `HubTable` com paginação e ordenação no servidor. */
export function EmbarcacoesTabelaView({
  items,
  total,
  isFirstLoad,
  status,
  errorMessage,
  reload,
  table,
  settings,
  colunas,
  dataTableProps,
  hasSearch,
  hasFilters,
  onClear,
  onDetail,
}: EmbarcacoesTabelaProps) {
  /** Conteúdo da célula: a lupa fecha a linha; a Situação é texto simples. */
  function renderCell(key: EmbarcacoesColumnKey, linha: EmbarcacaoLinha): ReactNode {
    switch (key) {
      case "acoes":
        return (
          <HubTableDetailButton
            label={linha.detailLabel}
            testId={linha.detailTestId}
            onDetail={() => onDetail(linha)}
            opensDialog
          />
        );
      case "situacao":
        return linha.situacao;
      default:
        return linha.embarcacao[key];
    }
  }

  return (
    <HubTableCartao<EmbarcacaoLinha, EmbarcacoesColumnKey, EmbarcacoesSortField>
      value={items}
      totalRecords={total}
      table={table}
      settings={settings}
      status={status}
      errorMessage={errorMessage}
      loadingLabel={EMBARCACOES_LOADING_LABEL}
      onRowClick={onDetail}
      emptyNoun="embarcação"
      emptyGender="f"
      hasSearch={hasSearch}
      hasFilters={hasFilters}
      onClear={onClear}
      dataTableProps={dataTableProps}
      rowsPerPageLabel={EMBARCACOES_ROWS_PER_PAGE_LABEL}
      testId="embarcacoes"
      data-testid="embarcacoes-table-wrapper"
      data-first-load={isFirstLoad || undefined}
    >
      {colunas.map((coluna) => (
        <HubTable.Column<EmbarcacaoLinha, EmbarcacoesColumnKey>
          key={coluna.key}
          field={coluna.key}
          columnKey={coluna.key}
          header={coluna.header}
          sortable={coluna.sortable}
          align={coluna.key === EMBARCACOES_ACTIONS_KEY ? "center" : undefined}
          alignHeader={coluna.key === EMBARCACOES_ACTIONS_KEY ? "center" : undefined}
          bodyClassName={
            coluna.key === EMBARCACOES_ACTIONS_KEY ? hubTableCartao.actionCellClassName : undefined
          }
          headerClassName={
            coluna.key === EMBARCACOES_ACTIONS_KEY ? hubTableCartao.actionCellClassName : undefined
          }
          style={{ width: coluna.width, minWidth: coluna.width }}
          body={(linha) => renderCell(coluna.key, linha)}
        />
      ))}
      <HubTable.Error>
        <ErrorState
          message={EMBARCACOES_ERROR_MESSAGE}
          description={errorMessage ?? undefined}
          onRetry={reload}
          testId="embarcacoes-error"
        />
      </HubTable.Error>
    </HubTableCartao>
  );
}
